import React, { memo, forwardRef, useMemo } from 'react';
import { useIntl } from 'umi';
import classNames from 'classnames';
import { useBoolean } from 'ahooks';
import { useItemAutoScroll } from '../hooks/useItemAutoScroll';
import agentAccordionExpandArrow from '@/pages/aigc/assets/svg/agent-accordion-expand-arrow.svg';

/**
 * ExpandableContainer 组件属性接口
 */
export interface IExpandableContainerProps {
  /** 是否展开 */
  isOpen: boolean;
  /** 最大高度 */
  maxHeight?: number;
  /** 自定义类名 */
  className?: string;
  /** 子元素 */
  children: React.ReactNode;
  /** 是否手动打开 */
  isManualOpen: boolean | undefined;
  /** 流式输出状态 */
  streamingState?: string;
  /** 思考ID */
  thinkId: string;
  /** 添加到手动列表的方法 */
  addToManualList: (id: string) => void;
}

export const ExpandableContainer = memo(
  forwardRef<HTMLDivElement, IExpandableContainerProps>(
    ({ isOpen, isManualOpen, maxHeight = 215, className = '',streamingState, thinkId, addToManualList, children }) => {
      const { formatMessage } = useIntl();
      const [isExpanded, { toggle }] = useBoolean(false);
      // 独立的自动滚动 
      const { containerRef } = useItemAutoScroll({
        isStreaming: streamingState === 'pending',
        isOpen,
      });
      // 检测内容高度是否超过指定高度
      const needsExpand = useMemo(() => {
        if (isOpen && containerRef?.current) {
          return containerRef?.current?.scrollHeight >= maxHeight;
        };
      }, [isOpen, maxHeight, containerRef?.current?.scrollHeight]);
      
      // 处理展开/收起按钮点击
      const handleToggleClick = () => {
        // 点击展开全部时，添加到手动列表，避免被自动收起
        if (!isExpanded) {
          addToManualList(thinkId);
        }
        toggle();
      };

      // 当需要展开且不是手动打开整个accordion时，显示按钮（暂时屏蔽"收起"入口，保留下方收起 DOM 以便后续恢复）
      const isShowNeedBtn = needsExpand && !isManualOpen && !isExpanded;
      return (
        <>
          <div ref={containerRef} className={classNames("dba-switch-agent-accordion-content-items-container", className,
            {
              "dba-switch-agent-accordion-content-items-open": (needsExpand && isExpanded) || isManualOpen,
              "dba-switch-agent-accordion-content-items-before": isShowNeedBtn && !isExpanded
            }
          )}>
            {children}
          </div>

          {isShowNeedBtn && (
            <div className="dba-expand-button-container">
              <div className="dba-expand-button" onClick={handleToggleClick}>
                {isExpanded
                  ? formatMessage({ id: 'aigc_common_retract' })
                  : formatMessage({ id: 'aigc_common_expand_all' })}
                <img
                  src={agentAccordionExpandArrow}
                  alt=""
                  className="dba-expand-button__icon"
                  style={{
                    transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                    transition: 'transform 0.2s ease',
                  }}
                />
              </div>
            </div>
          )}
        </>
      );
    },
  ),
);
